import { z } from 'zod';
import { paginatedResponse, decimalString } from './common';
import { createDrawerRequest } from './project-drawers';
import { cabinetConstructionMode, cabinetPieceInput, MAX_CABINET_PIECES } from './project-cabinets';

/**
 * Schemas for the /catalog-items resource.
 *
 * `CatalogItem` is the workshop's reusable template library: a single table
 * discriminated by `type` (CABINET / PANEL / COUNTERTOP / ELEMENT / LIGHTING /
 * HARDWARE / DRAWER). Per-project line items (`Cabinet`, `Panel`, ...) snapshot a
 * catalog item via `catalogItemId`.
 *
 * - `defaultCostNet` is hidden from callers without `CATALOG_READ_COST` (workers).
 *   The API omits the field at DTO time; on the wire it is therefore optional.
 * - `type` is immutable post-creation (load-bearing for the per-project tables).
 * - `attributes` is a free-form Json column reserved for type-specific fields
 *   (added to the UI in later PRs); preserved on update if not provided.
 */

export const catalogItemTypeEnum = z.enum([
  'CABINET',
  'PANEL',
  'COUNTERTOP',
  'ELEMENT',
  'LIGHTING',
  'HARDWARE',
  'DRAWER',
  'MATERIAL',
]);
export type CatalogItemTypeValue = z.infer<typeof catalogItemTypeEnum>;

export const catalogCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type CatalogCurrencyValue = z.infer<typeof catalogCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

const positiveDecimalString = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});
const positiveIntegerString = z
  .string()
  .regex(/^\d+(\.0+)?$/, 'Expected positive integer')
  .refine((v) => Number.isInteger(Number(v)) && Number(v) > 0, {
    message: 'Must be greater than 0',
  });

const optionalDimMm = z.number().int().nonnegative().max(100_000).optional().nullable();

export const catalogCabinetHardwareItem = z.object({
  hardwareCatalogId: z.string().cuid(),
  quantity: positiveDecimalString.default('1'),
  coefficient: positiveDecimalString.default('1.00'),
  name: z.string().trim().max(200).optional(),
  sku: z.string().trim().max(80).nullable().optional(),
  manufacturer: z.string().trim().max(120).nullable().optional(),
  pricePerPiece: decimalString.optional(),
  currency: catalogCurrencyEnum.optional(),
});
export type CatalogCabinetHardwareItem = z.infer<typeof catalogCabinetHardwareItem>;

export const catalogCabinetAttributes = z.object({
  materialId: z.string().cuid().nullable().optional(),
  edgeId: z.string().cuid().nullable().optional(),
  frontMaterialId: z.string().cuid().nullable().optional(),
  frontEdgeId: z.string().cuid().nullable().optional(),
  backMaterialId: z.string().cuid().nullable().optional(),
  backEdgeId: z.string().cuid().nullable().optional(),
  backHasEdge: z.boolean().optional(),
  frontMaterial: optionalTrim(100).nullable().optional(),
  backMaterial: optionalTrim(100).nullable().optional(),
  edging: optionalTrim(100).nullable().optional(),
  intermediarCount: z.number().int().min(0).max(10_000).nullable().optional(),
  politeCount: z.number().int().min(0).max(10_000).nullable().optional(),
  joinType: optionalTrim(100).nullable().optional(),
  intermediarDoubled: z.boolean().optional(),
  carcassDoubled: z.boolean().optional(),
  carcassExteriorFront: z.boolean().optional(),
  politeDoubled: z.boolean().optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  joinTypeKind: z
    .enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE'])
    .nullable()
    .optional(),
  quantity: positiveDecimalString.optional(),
  coefficient: positiveDecimalString.optional(),
  /**
   * Per-board markups snapshotted by the blueprint. Absent on anything saved
   * before the coefficient moved onto the material, which the migration
   * backfills from the old `coefficient` — left unset, an old blueprint would
   * hand its 2.5 to an AUTO row that marks up per material and double it.
   */
  carcassCoefficient: positiveDecimalString.optional(),
  frontCoefficient: positiveDecimalString.optional(),
  backCoefficient: positiveDecimalString.optional(),
  hardwareItems: z.array(catalogCabinetHardwareItem).max(50).optional(),
  drawers: z.array(createDrawerRequest).max(50).optional(),
  // Cut-list ("Pe piese") blueprints. Absent/PARAMETRIC on every pre-existing
  // blueprint, so old attributes still parse unchanged.
  constructionMode: cabinetConstructionMode.optional(),
  pieces: z.array(cabinetPieceInput).max(MAX_CABINET_PIECES).optional(),
});
export type CatalogCabinetAttributes = z.infer<typeof catalogCabinetAttributes>;

export const catalogPanelAttributes = z.object({
  materialId: z.string().cuid().nullable().optional(),
  edgeId: z.string().cuid().nullable().optional(),
  frontMaterialId: z.string().cuid().nullable().optional(),
  frontEdgeId: z.string().cuid().nullable().optional(),
  material: optionalTrim(100).nullable().optional(),
  edging: optionalTrim(100).nullable().optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  doubled: z.boolean().optional(),
  cx2: z.boolean().optional(),
  quantity: positiveIntegerString.optional(),
  coefficient: positiveDecimalString.optional(),
  materialCoefficient: positiveDecimalString.optional(),
});
export type CatalogPanelAttributes = z.infer<typeof catalogPanelAttributes>;

export const catalogCountertopAttributes = z.object({
  materialId: z.string().cuid().nullable().optional(),
  edgeId: z.string().cuid().nullable().optional(),
  material: optionalTrim(100).nullable().optional(),
  edgeProfile: optionalTrim(100).nullable().optional(),
  doubled: z.boolean().optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  quantity: positiveIntegerString.optional(),
  coefficient: positiveDecimalString.optional(),
  materialCoefficient: positiveDecimalString.optional(),
});
export type CatalogCountertopAttributes = z.infer<typeof catalogCountertopAttributes>;

export const catalogHardwareAttributes = z.object({
  hardwareCatalogId: z.string().cuid().nullable().optional(),
  hardwareName: optionalTrim(200).nullable().optional(),
  sku: optionalTrim(80).nullable().optional(),
  manufacturer: optionalTrim(120).nullable().optional(),
  pricePerPiece: decimalString.optional(),
  currency: catalogCurrencyEnum.optional(),
  quantity: positiveIntegerString.optional(),
  coefficient: positiveDecimalString.optional(),
});
export type CatalogHardwareAttributes = z.infer<typeof catalogHardwareAttributes>;

export const catalogLightingAttributes = z.object({
  lightingCatalogId: z.string().cuid().nullable().optional(),
  lightingName: optionalTrim(200).nullable().optional(),
  sku: optionalTrim(80).nullable().optional(),
  manufacturer: optionalTrim(120).nullable().optional(),
  pricePerPiece: decimalString.optional(),
  currency: catalogCurrencyEnum.optional(),
  quantity: positiveIntegerString.optional(),
  coefficient: positiveDecimalString.optional(),
});
export type CatalogLightingAttributes = z.infer<typeof catalogLightingAttributes>;

export const catalogItemListItem = z.object({
  id: z.string(),
  type: catalogItemTypeEnum,
  name: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  defaultWidthMm: z.number().int().nullable(),
  defaultHeightMm: z.number().int().nullable(),
  defaultDepthMm: z.number().int().nullable(),
  thicknessMm: z.number().int().nullable(),
  material: z.string().nullable(),
  color: z.string().nullable(),
  finish: z.string().nullable(),
  manufacturer: z.string().nullable(),
  cabinetType: z.string().nullable(),
  room: z.string().nullable(),
  materialCategory: z.string().nullable(),
  structure: z.string().nullable(),
  // Cost is only present when the caller has CATALOG_READ_COST.
  defaultCostNet: decimalString.optional(),
  currency: catalogCurrencyEnum,
  deletedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CatalogItemListItem = z.infer<typeof catalogItemListItem>;

export const catalogItemDetails = catalogItemListItem.extend({
  // Free-form per-type attributes; null when unused. Only present when the
  // caller has at least CATALOG_READ.
  attributes: z.record(z.unknown()).nullable(),
});
export type CatalogItemDetails = z.infer<typeof catalogItemDetails>;

export const listCatalogItemsResponse = paginatedResponse(catalogItemListItem);
export type ListCatalogItemsResponse = z.infer<typeof listCatalogItemsResponse>;

export const listCatalogItemsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(100).optional(),
  type: catalogItemTypeEnum.optional(),
  includeDeleted: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .default(false),
});
export type ListCatalogItemsQuery = z.infer<typeof listCatalogItemsQuery>;

export const createCatalogItemRequest = z.object({
  type: catalogItemTypeEnum,
  name: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(50),
  description: optionalTrim(2000),
  defaultWidthMm: optionalDimMm,
  defaultHeightMm: optionalDimMm,
  defaultDepthMm: optionalDimMm,
  thicknessMm: z.number().int().nonnegative().max(1000).optional().nullable(),
  material: optionalTrim(100),
  color: optionalTrim(50),
  finish: optionalTrim(100),
  manufacturer: optionalTrim(150),
  cabinetType: optionalTrim(100),
  room: optionalTrim(100),
  materialCategory: optionalTrim(100),
  structure: optionalTrim(100),
  defaultCostNet: decimalString.optional(),
  currency: catalogCurrencyEnum.default('RON'),
  attributes: z.record(z.unknown()).optional().nullable(),
});
export type CreateCatalogItemRequest = z.infer<typeof createCatalogItemRequest>;

// `type` intentionally omitted: immutable post-creation.
export const updateCatalogItemRequest = createCatalogItemRequest.omit({ type: true }).partial();
export type UpdateCatalogItemRequest = z.infer<typeof updateCatalogItemRequest>;
